/**
 * React Query hooks for AgentBuilder server state.
 *
 * Wraps the GenAI API utilities and generated appserver API hooks as queries and mutations.
 * Server-cache state (currentAgentBuilder, versions) lives here; UI-only state
 * (selectedNodeId, isDirty, selectedVersionId, draftBaseVersionId) lives in the
 * Zustand store at agentBuilder/agentBuilder.store.ts.
 */

import {
  type QueryClient,
  type UseMutationOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  AGENT_BUILDER_LIST_PARAMS,
  discardAgentBuilderDraft,
  getAgentBuilder,
  getAgentBuilderTemplates,
  getAgentBuilderVersions,
  mapCoreSummaryToListItem,
  publishAgentBuilderVersion,
  saveAgentBuilderDraft,
  setAgentBuilderVersionProduction,
  updateAgentBuilderMetadata,
} from '@/agentBuilder/agentBuilder.utilities';
import {
  GetCustomAgentTriggerQueryKey,
  GetCustomAgentsQueryKey,
  useApiDeleteCustomAgentTriggerMutation,
  useApiGetCustomAgentTriggerQuery,
  useApiGetCustomAgentsQuery,
  useApiPutCustomAgentTriggerMutation,
} from '@seeqdev/api/endpoint/CustomAgentsApi';
import { KindEnum as CustomAgentTriggerKindEnum } from '@seeqdev/api/model/CustomAgentTriggerInputV1';
import type { AgentTileAppearanceChange } from '@/agentBuilder/AgentIconTilePicker.molecule';
import type {
  AgentBuilderEdge,
  AgentBuilderGraph,
  AgentBuilderMeta,
  AgentBuilderNode,
  AgentBuilderScheduleTrigger,
  AgentBuilderVersion,
  CoreCustomAgentSummary,
} from '@/agentBuilder/agentBuilder.types';
import type { CustomAgentMetadataUpdateInputV1 } from '@seeqdev/api/model/CustomAgentMetadataUpdateInputV1';
import type { CustomAgentSummaryOutputV1 } from '@seeqdev/api/model/CustomAgentSummaryOutputV1';
import type { CustomAgentTriggerOutputV1 } from '@seeqdev/api/model/CustomAgentTriggerOutputV1';

/**
 * Query key constants. Keep keys symmetric so invalidation in one mutation
 * cascades correctly (e.g., publishing a version invalidates both the agent
 * builder detail and the versions list for that id).
 */
export const AGENT_BUILDER_QUERY_KEYS = {
  detail: (id: string) => ['agent-builder', id] as const,
  templates: ['agent-builder-templates'] as const,
  versions: (id: string) => ['agent-builder-versions', id] as const,
};

// === Queries ===

type GetAgentBuilderOptions = {
  versionId?: string;
};

/**
 * Read the cached agent builder graph for an id. Disabled when id is falsy or
 * 'new' (the page handles the create-new-then-redirect flow before this hook
 * becomes relevant).
 *
 * staleTime is Infinity because the page imperatively populates the cache via
 * useSetAgentBuilderCache during its load/save/publish/revert flows — those
 * paths normalize nodes and edges and emit telemetry, so they own the write
 * side. This hook is the read side: consumers (page, AiAssistant, Header)
 * subscribe to the cache without triggering background refetches that would
 * race with imperative writes.
 */
export const useAgentBuilderQuery = (id: string | undefined, options?: GetAgentBuilderOptions) => {
  const versionId = options?.versionId;
  return useQuery({
    queryKey: versionId
      ? ([...AGENT_BUILDER_QUERY_KEYS.detail(id ?? ''), 'version', versionId] as const)
      : AGENT_BUILDER_QUERY_KEYS.detail(id ?? ''),
    queryFn: () => getAgentBuilder(id!, options),
    enabled: !!id && id !== 'new',
    staleTime: Infinity,
  });
};

export const useAgentBuilderTemplatesQuery = (enabled = true) =>
  useQuery({
    queryKey: AGENT_BUILDER_QUERY_KEYS.templates,
    queryFn: getAgentBuilderTemplates,
    enabled,
    staleTime: 30_000,
  });

/** Id lookup memoized on the response array, so all per-message subscribers share one index. */
const customAgentIndexes = new WeakMap<CustomAgentSummaryOutputV1[], Map<string, CustomAgentSummaryOutputV1>>();

const findCustomAgentById = (items: CustomAgentSummaryOutputV1[], id: string) => {
  let index = customAgentIndexes.get(items);
  if (!index) {
    index = new Map(items.map((item) => [item.id, item]));
    customAgentIndexes.set(items, index);
  }
  return index.get(id);
};

/** Read one custom agent out of the shared list cache, scoped by `select` to that id. */
export const useAgentBuilderListItemQuery = (id: string | undefined, enabled = true) => {
  const query = useApiGetCustomAgentsQuery(AGENT_BUILDER_LIST_PARAMS, {
    enabled: enabled && Boolean(id),
    staleTime: 30_000,
    select: (items) => {
      const match = id ? findCustomAgentById(items, id) : undefined;
      return match ? [match] : [];
    },
  });
  const [match] = query.data ?? [];
  return { ...query, data: match ? mapCoreSummaryToListItem(match) : undefined };
};

/** Fetch the version history for an agent builder. Newest first. */
export const useAgentBuilderVersionsQuery = (id: string | undefined) =>
  useQuery({
    queryKey: AGENT_BUILDER_QUERY_KEYS.versions(id ?? ''),
    queryFn: () => getAgentBuilderVersions(id!),
    enabled: !!id,
  });

/** Fetch the schedule trigger for a custom agent. A missing schedule is represented as null. */
export const useAgentBuilderScheduleQuery = (id: string | undefined) => {
  const query = useApiGetCustomAgentTriggerQuery(
    { id: id ?? '' },
    {
      enabled: !!id && id !== 'new',
      // Trigger reads are user-refreshable, so surface failures immediately instead of delaying the
      // UI on retries. A missing schedule is returned as an empty successful response, not a 404.
      retry: false,
    },
  );
  return {
    ...query,
    data: query.data?.kind === CustomAgentTriggerKindEnum.Schedule ? (query.data.schedule ?? null) : null,
  };
};

// === Mutations ===

/** Serializes name and icon writes so they cannot overlap or land out of order. */
const METADATA_MUTATION_SCOPE = { id: 'agent-builder-metadata' } as const;

type UpdateAgentBuilderMetadataVariables<TUpdates extends CustomAgentMetadataUpdateInputV1> = {
  id: string;
  updates: TUpdates;
};

/**
 * Persist name, icon, or color. Callers narrow `TUpdates` so `onError` can read
 * the attempted value without a cast.
 *
 * Throws on null because `updateAgentBuilderMetadata` otherwise never hits
 * `onError`. Does not write the cache on success (CRAB-57591); callers patch
 * optimistically and roll back in `onError`.
 */
export const useUpdateAgentBuilderMetadataMutation = <TUpdates extends CustomAgentMetadataUpdateInputV1>(
  options: Pick<
    UseMutationOptions<AgentBuilderGraph, Error, UpdateAgentBuilderMetadataVariables<TUpdates>>,
    'onSuccess' | 'onError'
  >,
) =>
  useMutation({
    scope: METADATA_MUTATION_SCOPE,
    mutationFn: async ({ id, updates }: UpdateAgentBuilderMetadataVariables<TUpdates>) => {
      const updated = await updateAgentBuilderMetadata(id, updates);
      if (!updated) {
        throw new Error(`Failed to update agent builder metadata ${id}`);
      }
      return updated;
    },
    ...options,
  });

type SaveAgentBuilderDraftVariables = {
  id: string;
  updates: {
    name?: string;
    nodes?: AgentBuilderNode[];
    edges?: AgentBuilderEdge[];
    meta?: AgentBuilderMeta;
  };
};

/**
 * Persist agent builder edits to the server.
 *
 * IMPORTANT: callers should NOT echo the mutation result back into a controlled
 * input's `value` prop. Doing so was the root cause of the focus-loss bug fixed
 * in CRAB-57591 — the user keeps typing while the mutation is in flight, and
 * overwriting the input with the server's stale response steals focus and
 * rewinds characters.
 *
 * The recommended pattern is to write the mutation result into the query cache
 * (which keeps server state coherent for queries) but to leave the typing
 * surface bound to local debounced state via DebouncedInput / DebouncedTextArea.
 */
export const useSaveAgentBuilderDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: SaveAgentBuilderDraftVariables) => saveAgentBuilderDraft(id, updates),
    onSuccess: (data, variables) => {
      if (data) {
        queryClient.setQueryData(AGENT_BUILDER_QUERY_KEYS.detail(variables.id), data);
      }
    },
  });
};

/**
 * Discard the in-progress draft and return to the last published version.
 *
 * Patches `meta.hasDraft` and `meta.draftBaseVersionId` on the cached graph
 * via setQueryData rather than calling invalidateQueries on the detail key.
 * Invalidation would trigger a background refetch of the unversioned graph
 * that races with callers (handleConfirmRevert, handleVersionClick) which
 * immediately fetch a specific historical version and write it to the same
 * cache key — the late-arriving unversioned refetch could clobber the
 * versioned, normalized graph the page just wrote. setQueryData is
 * synchronous and deterministic; no refetch, no race.
 *
 * The versions query is still invalidated; it isn't subject to the
 * normalize-then-write pattern the detail key has.
 */
export const useDiscardAgentBuilderDraftMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => discardAgentBuilderDraft(id),
    onSuccess: (_data, id) => {
      queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
        if (!current) return current;
        return {
          ...current,
          meta: { ...current.meta, hasDraft: false, draftBaseVersionId: null, draftUpdatedAt: undefined },
        };
      });
      void queryClient.invalidateQueries({ queryKey: AGENT_BUILDER_QUERY_KEYS.versions(id) });
    },
  });
};

/**
 * Publish the current draft as a new version. The mutation returns the
 * refreshed version list; we update the versions cache directly and patch
 * `meta.hasDraft` / `meta.draftBaseVersionId` on the cached graph via
 * setQueryData. Same race-avoidance reasoning as
 * useDiscardAgentBuilderDraftMutation: invalidation would refetch the detail
 * query in parallel with the page's own setAgentBuilderCache write that
 * follows publish, and the late-arriving refetch could overwrite the
 * normalized post-publish graph with raw server data.
 */
export const usePublishAgentBuilderVersionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => publishAgentBuilderVersion(id),
    onSuccess: (versions, id) => {
      queryClient.setQueryData<AgentBuilderVersion[]>(AGENT_BUILDER_QUERY_KEYS.versions(id), versions);
      queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
        if (!current) return current;
        return {
          ...current,
          meta: { ...current.meta, hasDraft: false, draftBaseVersionId: null, draftUpdatedAt: undefined },
        };
      });
      // The production version determines whether a saved schedule is effectively enabled,
      // so automation status consumers must refetch after a publish.
      void queryClient.invalidateQueries({ queryKey: GetCustomAgentTriggerQueryKey({ id }) });
    },
  });
};

type SetProductionVariables = {
  id: string;
  versionId: string;
};

/** Mark a specific version as the production version. Returns the updated versions list. */
export const useSetAgentBuilderVersionProductionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionId }: SetProductionVariables) => setAgentBuilderVersionProduction(id, versionId),
    onSuccess: (versions, variables) => {
      queryClient.setQueryData<AgentBuilderVersion[]>(AGENT_BUILDER_QUERY_KEYS.versions(variables.id), versions);
      // The production version determines whether a saved schedule is effectively enabled,
      // so automation status consumers must refetch after switching production.
      void queryClient.invalidateQueries({ queryKey: GetCustomAgentTriggerQueryKey({ id: variables.id }) });
    },
  });
};

type SaveAgentBuilderScheduleVariables = {
  id: string;
  schedule: Pick<AgentBuilderScheduleTrigger, 'cronSchedule' | 'timezone' | 'enabled'>;
};

/** Create or update the schedule trigger and keep all schedule consumers in sync. */
export const useSaveAgentBuilderScheduleMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useApiPutCustomAgentTriggerMutation({
    onSuccess: (trigger, variables) => {
      const [, { id }] = variables;
      queryClient.setQueryData<CustomAgentTriggerOutputV1>(GetCustomAgentTriggerQueryKey({ id }), trigger);
    },
  });
  return {
    ...mutation,
    mutateAsync: async ({ id, schedule }: SaveAgentBuilderScheduleVariables): Promise<AgentBuilderScheduleTrigger> => {
      const trigger = await mutation.mutateAsync([{ kind: CustomAgentTriggerKindEnum.Schedule, schedule }, { id }]);
      if (trigger.kind !== CustomAgentTriggerKindEnum.Schedule || !trigger.schedule) {
        throw new Error('Agent builder schedule trigger response did not include a schedule');
      }
      return trigger.schedule;
    },
  };
};

/** Delete the schedule trigger and clear it from the shared schedule cache. */
export const useDeleteAgentBuilderScheduleMutation = () => {
  const queryClient = useQueryClient();
  const mutation = useApiDeleteCustomAgentTriggerMutation({
    onSuccess: (_data, variables) => {
      const [{ id }] = variables;
      queryClient.setQueryData<CustomAgentTriggerOutputV1 | null>(GetCustomAgentTriggerQueryKey({ id }), null);
    },
  });
  return {
    ...mutation,
    mutateAsync: async (id: string): Promise<void> => {
      await mutation.mutateAsync([{ id }]);
    },
  };
};

/**
 * Imperatively replace the cached agent builder. Used by load/create paths that
 * normalize nodes and edges before handing off to the editor — those paths
 * already have the data in hand and shouldn't trigger a refetch.
 */
export const useSetAgentBuilderCache = () => {
  const queryClient = useQueryClient();
  return (graph: AgentBuilderGraph) => {
    queryClient.setQueryData(AGENT_BUILDER_QUERY_KEYS.detail(graph.id), graph);
  };
};

type AgentBuilderDraftCachePatch = Pick<AgentBuilderGraph, 'id' | 'nodes' | 'edges' | 'meta'>;

/** Patch autosaved draft graph fields without overwriting separately-saved metadata. */
export const useSetAgentBuilderDraftCache = () => {
  const queryClient = useQueryClient();
  return ({ id, nodes, edges, meta }: AgentBuilderDraftCachePatch) => {
    queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) =>
      current ? { ...current, nodes, edges, meta } : current,
    );
  };
};

/**
 * Patch the `name` field on the cached agent builder. History restore can
 * also provide metadata so undo/redo can return to a clean no-draft snapshot
 * without rewriting the rest of the graph.
 */
export const useSetAgentBuilderName = () => {
  const queryClient = useQueryClient();
  return (id: string, name: string, meta?: AgentBuilderMeta) => {
    queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
      if (!current) return current;
      return { ...current, name, ...(meta ? { meta } : {}) };
    });
  };
};

type AgentBuilderListItemPatch = Partial<Pick<CoreCustomAgentSummary, 'name' | 'icon' | 'iconColor'>>;

const patchAgentBuilderListItem = (queryClient: QueryClient, id: string, patch: AgentBuilderListItemPatch) => {
  queryClient.setQueryData<CoreCustomAgentSummary[] | undefined>(
    GetCustomAgentsQueryKey(AGENT_BUILDER_LIST_PARAMS),
    (current) => current?.map((item) => (item.id === id ? { ...item, ...patch } : item)),
  );
};

export const useGetAgentBuilderAppearance = () => {
  const queryClient = useQueryClient();
  return (id: string): AgentTileAppearanceChange | undefined => {
    const cached = queryClient.getQueryData<AgentBuilderGraph | null>(AGENT_BUILDER_QUERY_KEYS.detail(id));
    return cached ? { icon: cached.icon, iconColor: cached.iconColor } : undefined;
  };
};

export const useSetAgentBuilderAppearance = () => {
  const queryClient = useQueryClient();
  return (id: string, { icon, iconColor }: AgentTileAppearanceChange) => {
    queryClient.setQueryData<AgentBuilderGraph | null | undefined>(AGENT_BUILDER_QUERY_KEYS.detail(id), (current) => {
      if (!current) return current;
      return { ...current, icon, iconColor };
    });
    patchAgentBuilderListItem(queryClient, id, { icon, iconColor });
  };
};

export const useSetAgentBuilderListItemName = () => {
  const queryClient = useQueryClient();
  return (id: string, name: string) => {
    patchAgentBuilderListItem(queryClient, id, { name });
  };
};
